import React, {useEffect, useState} from 'react'
import SpielDetailsDialog from "./SpielDetailsDialog";
import {rundenSpiel, Spiel} from "./SchweizerRundenBereich";
import {useSchmaleAnsicht} from "./statistikWerte";

const ROTATION_SEKUNDEN = 10;

/**
 * Gruppen im Gruppenturnier für die neue öffentliche Ansicht (nur lesen): je Gruppe eine Karte mit Tabelle und Spielen
 * (nach Spielrunden). Auswahl einzelner Gruppen, "Meine Gruppe", automatisches Durchschalten (z.B. für einen Bildschirm),
 * Tabellen ein/aus und bereits gespielte Spiele ausblenden.
 */
export default function GruppenBereich({tournament, gruppenTabellen, bestplatzierteDtos, boardGames, liveGames, userId}) {

    // undefined = alle Gruppen
    const [auswahl, setAuswahl] = useState(undefined);
    const [rotation, setRotation] = useState(false);
    const [zeigeTabellen, setZeigeTabellen] = useState(true);
    const [gespielteAusblenden, setGespielteAusblenden] = useState(false);
    const [markierterSpieler, setMarkierterSpieler] = useState(undefined);
    const [statistikGame, setStatistikGame] = useState(undefined);
    const schmal = useSchmaleAnsicht();

    const anzahlGruppen = tournament.anzahlGruppen || 0;
    const modusGruppenphase = tournament.tournamentModusEntities.find(element => element.modusTyp === "gruppenturnier");
    const isSetModus = modusGruppenphase !== undefined && modusGruppenphase.sets > 1;
    const gruppenphaseBeendet = vorhanden(tournament.goldTurnierId);
    // nach der Gruppenphase sind keine Boards mehr vergeben
    const aktuelleBoardGames = gruppenphaseBeendet ? [] : boardGames;
    const meineGruppe = ermittleMeineGruppe(tournament, userId);

    // automatisch durchschalten: alle paar Sekunden die nächste Gruppe
    useEffect(() => {
        if (!rotation || anzahlGruppen < 2) {
            return;
        }
        const intervall = setInterval(() => setAuswahl(alt => alt === undefined || alt >= anzahlGruppen ? 1 : alt + 1), ROTATION_SEKUNDEN * 1000);
        return () => clearInterval(intervall);
    }, [rotation, anzahlGruppen]);

    const spieler = (playerId) => tournament.tournamentPlayerEntities.find(p => p.id === playerId);
    const istInaktiv = (playerId) => {
        const p = spieler(playerId);
        return p !== undefined && p.inaktiv === true;
    }
    const spielerName = (playerId) => {
        const p = spieler(playerId);
        if (!p) {
            return "";
        }
        return p.inaktiv ? <s>{p.name}</s> : p.name;
    }

    const waehle = (gruppe) => {
        setRotation(false);
        setAuswahl(gruppe);
    }

    const getAuswahlLeiste = () => {
        const eintraege = [{id: "alle", label: "Alle", aktiv: auswahl === undefined && !rotation, waehle: () => waehle(undefined)}];
        if (meineGruppe !== undefined) {
            eintraege.push({id: "meine", label: "Meine Gruppe", aktiv: auswahl === meineGruppe && !rotation, waehle: () => waehle(meineGruppe)});
        }
        for (let i = 1; i <= anzahlGruppen; i++) {
            eintraege.push({id: "gruppe" + i, label: String(i), titel: "Gruppe " + i, aktiv: auswahl === i && !rotation, waehle: () => waehle(i)});
        }
        return <div className="dhpt-segment dhpt-gr-auswahl" role="radiogroup" aria-label="Gruppe">
            {eintraege.map(e => <button key={e.id} type="button" role="radio" aria-checked={e.aktiv} title={e.titel}
                                        aria-label={e.titel} className="dhpt-segment-knopf" onClick={e.waehle}>{e.label}</button>)}
        </div>
    }

    const getSchalter = (id, label, checked, onChange) => <label className="dhpt-gr-schalter" htmlFor={id}>
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}/>
        <span>{label}</span>
    </label>;

    const sichtbareGruppen = [];
    for (let i = 1; i <= anzahlGruppen; i++) {
        if (auswahl === undefined || auswahl === i) {
            sichtbareGruppen.push(i);
        }
    }

    return <div className="dhpt-inhalt-eigen dhpt-sr dhpt-gr">
        <div className="dhpt-sr-leiste">
            {anzahlGruppen > 1 && getAuswahlLeiste()}
            {gruppenphaseBeendet && <span className="dhpt-chip dhpt-chip-live">Gruppenphase beendet</span>}
        </div>
        <div className="dhpt-gr-optionen">
            {getSchalter("dhpt-gr-tabellen", "Tabellen", zeigeTabellen, setZeigeTabellen)}
            {getSchalter("dhpt-gr-gespielt", "Gespielte Spiele ausblenden", gespielteAusblenden, setGespielteAusblenden)}
            {anzahlGruppen > 1 && getSchalter("dhpt-gr-rotation", "Gruppen automatisch durchschalten (" + ROTATION_SEKUNDEN + " s)", rotation, (wert) => {
                setRotation(wert);
                if (wert && auswahl === undefined) {
                    setAuswahl(1);
                }
            })}
        </div>

        <div className={sichtbareGruppen.length > 1 ? "dhpt-gr-gruppen" : "dhpt-gr-gruppen dhpt-gr-gruppen-einzeln"}>
            {sichtbareGruppen.map(gruppe => <Gruppe key={gruppe} gruppe={gruppe} tournament={tournament} isSetModus={isSetModus} schmal={schmal}
                                                    tabelle={(gruppenTabellen || []).find(t => t.gruppe === gruppe)}
                                                    bestplatzierteDtos={bestplatzierteDtos || []} zeigeTabelle={zeigeTabellen}
                                                    gespielteAusblenden={gespielteAusblenden} istInaktiv={istInaktiv} spielerName={spielerName}
                                                    boardGames={aktuelleBoardGames} liveGames={liveGames}
                                                    markierterSpieler={markierterSpieler} onMarkiere={setMarkierterSpieler}
                                                    onStatistik={setStatistikGame}/>)}
        </div>

        <p className="dhpt-hinweis">
            {legende(tournament).map(eintrag => <span key={eintrag.klasse} className="dhpt-gr-legende-eintrag">
                <span className={"dhpt-gr-legende " + eintrag.klasse}/>{eintrag.text}
            </span>)}
            <span className="dhpt-gr-legende-eintrag">Sortierung: Punkte, gewonnene Spiele, Leg-Differenz, gewonnene Legs, direkter Vergleich</span>
        </p>

        {statistikGame !== undefined &&
            <SpielDetailsDialog tournamentId={tournament.id} tournamentGameId={statistikGame.id} ergebnisVomScorer={statistikGame.ergebnisVomScorer === true}
                                 onClose={() => setStatistikGame(undefined)}/>
        }
    </div>
}

function Gruppe({gruppe, tournament, tabelle, isSetModus, schmal, bestplatzierteDtos, zeigeTabelle, gespielteAusblenden, istInaktiv, spielerName,
                    boardGames, liveGames, markierterSpieler, onMarkiere, onStatistik}) {

    const spiele = tournament.tournamentGroupGameEntities
        .filter(game => game.gruppe === gruppe)
        .filter(game => !gespielteAusblenden || !(
            (game.anzahlSpieler1 !== null && game.anzahlSpieler2 !== null) || game.spieler2 === null
            || istInaktiv(game.spieler1) || istInaktiv(game.spieler2)));
    // Spiele nach Spielrunde gruppieren
    const spielrunden = [];
    spiele.forEach(game => {
        const runde = game.spielrunde;
        let eintrag = spielrunden.find(r => r.runde === runde);
        if (!eintrag) {
            eintrag = {runde, spiele: []};
            spielrunden.push(eintrag);
        }
        eintrag.spiele.push(game);
    });
    spielrunden.sort((a, b) => (a.runde || 0) - (b.runde || 0));

    const rangliste = tabelle ? (vorhanden(tabelle.ranglisteManuell) && tabelle.ranglisteManuell.length > 0 ? tabelle.ranglisteManuell : tabelle.rangliste) : undefined;

    return <section className="dhpt-karte dhpt-gr-gruppe">
        <h3 className="dhpt-karte-titel"><span>Gruppe {gruppe}</span></h3>
        {zeigeTabelle && rangliste && rangliste.length > 0 &&
            <div className="dhpt-gr-teil">
                {schmal
                    ? <TabelleSchmal rangliste={rangliste} tournament={tournament} isSetModus={isSetModus} bestplatzierteDtos={bestplatzierteDtos}
                                     spielerName={spielerName} markierterSpieler={markierterSpieler} onMarkiere={onMarkiere}/>
                    : <Tabelle rangliste={rangliste} tournament={tournament} isSetModus={isSetModus} bestplatzierteDtos={bestplatzierteDtos}
                               spielerName={spielerName} markierterSpieler={markierterSpieler} onMarkiere={onMarkiere}/>}
            </div>
        }
        <div className="dhpt-gr-teil">
            {spielrunden.length === 0 && <p className="dhpt-hinweis">{gespielteAusblenden ? "Keine offenen Spiele." : "Keine Spiele vorhanden."}</p>}
            {/* Spielrunden ohne Überschrift, nur durch eine Linie getrennt */}
            {spielrunden.map(r => <div key={"runde_" + r.runde} className="dhpt-gr-spielrunde">
                <div className="dhpt-sr-spiele">
                    {r.spiele.map(game => <Spiel key={game.id} spiel={rundenSpiel(game)} statistikNurMitScoring={true} tournament={tournament}
                                                 spielerName={spielerName} boardGames={boardGames} liveGames={liveGames}
                                                 markierterSpieler={markierterSpieler} onMarkiere={onMarkiere} onStatistik={onStatistik}/>)}
                </div>
            </div>)}
        </div>
    </section>
}

// Farbe der Zeile: wer ins Gold-, Silber- oder Bronze-Turnier kommt; Bestplatzierte zählen zu Gold
function zeilenKlasse(platz, playerId, tournament, bestplatzierteDtos) {
    const gold = tournament.anzahlSpielerGold || 0;
    const silber = gold && tournament.anzahlSpielerSilber ? tournament.anzahlSpielerSilber : 0;
    const bronze = gold && silber && tournament.anzahlSpielerBronze ? tournament.anzahlSpielerBronze : 0;
    if (platz <= gold || bestplatzierteDtos.some(dto => dto.playerId === playerId)) {
        return "dhpt-gr-gold";
    }
    if (platz <= gold + silber) {
        return "dhpt-gr-silber";
    }
    if (platz <= gold + silber + bronze) {
        return "dhpt-gr-bronze";
    }
    return undefined;
}

function legende(tournament) {
    const eintraege = [];
    if (tournament.anzahlSpielerGold) {
        eintraege.push({klasse: "dhpt-gr-gold", text: tournament.anzahlSpielerSilber ? "Gold" : "kommt weiter"});
        if (tournament.anzahlSpielerSilber) {
            eintraege.push({klasse: "dhpt-gr-silber", text: "Silber"});
            if (tournament.anzahlSpielerBronze) {
                eintraege.push({klasse: "dhpt-gr-bronze", text: "Bronze"});
            }
        }
    }
    return eintraege;
}

function Tabelle({rangliste, tournament, isSetModus, bestplatzierteDtos, spielerName, markierterSpieler, onMarkiere}) {
    return <div className="dhpt-sr-tabelle-rahmen">
        <table className="dhpt-sr-tabelle">
            <thead>
            <tr>
                <th className="dhpt-sr-pos">#</th>
                <th className="dhpt-sr-name-spalte">Spieler</th>
                <th>Pkt</th>
                <th title="Siege">S</th>
                <th title="Unentschieden">U</th>
                <th title="Niederlagen">V</th>
                <th title={isSetModus ? "Sets gewonnen : verloren" : "Legs gewonnen : verloren"}>{isSetModus ? "Sets" : "Legs"}</th>
            </tr>
            </thead>
            <tbody>
            {rangliste.map((eintrag, index) => {
                const bestplatziert = bestplatzierteDtos.find(dto => dto.playerId === eintrag.playerid);
                const klassen = ["dhpt-sr-zeile", zeilenKlasse(index + 1, eintrag.playerid, tournament, bestplatzierteDtos)];
                if (markierterSpieler !== undefined && markierterSpieler === eintrag.playerid) {
                    klassen.push("dhpt-sr-markiert");
                }
                return <tr key={eintrag.playerid} className={klassen.filter(Boolean).join(" ")}
                           onMouseEnter={() => onMarkiere(eintrag.playerid)} onMouseLeave={() => onMarkiere(undefined)}>
                    <td className="dhpt-sr-pos">{index + 1}</td>
                    <td className="dhpt-sr-name-spalte">
                        {bestplatziert && bestplatziert.markierung &&
                            <i className="pi pi-question-circle dhpt-gr-auswahl-noetig" title="Zwischen allen Spielern mit diesem Zeichen muss eine Auswahl getroffen werden."
                               aria-label="Auswahl nötig"/>}
                        {spielerName(eintrag.playerid)}
                    </td>
                    <td className="dhpt-sr-punkte">{eintrag.punkte}</td>
                    <td>{eintrag.gewonnenespiele}</td>
                    <td>{eintrag.unentschiedenspiele}</td>
                    <td>{eintrag.verlorenespiele}</td>
                    <td className="dhpt-sr-legs">{eintrag.anzahlLegsGewonnen}:{eintrag.anzahlLegsVerloren}</td>
                </tr>
            })}
            </tbody>
        </table>
    </div>
}

// Handy: je Spieler eine Zeile mit Platz, Name und Punkten, die übrigen Werte klein darunter
function TabelleSchmal({rangliste, tournament, isSetModus, bestplatzierteDtos, spielerName, markierterSpieler, onMarkiere}) {
    return <ol className="dhpt-sr-liste" aria-label="Tabelle">
        {rangliste.map((eintrag, index) => {
            const klassen = ["dhpt-sr-listen-zeile", zeilenKlasse(index + 1, eintrag.playerid, tournament, bestplatzierteDtos)];
            if (markierterSpieler !== undefined && markierterSpieler === eintrag.playerid) {
                klassen.push("dhpt-sr-markiert");
            }
            const werte = "S/U/V " + eintrag.gewonnenespiele + "/" + eintrag.unentschiedenspiele + "/" + eintrag.verlorenespiele
                + " · " + (isSetModus ? "Sets " : "Legs ") + eintrag.anzahlLegsGewonnen + ":" + eintrag.anzahlLegsVerloren;
            return <li key={eintrag.playerid} className={klassen.filter(Boolean).join(" ")}
                       onClick={() => onMarkiere(markierterSpieler === eintrag.playerid ? undefined : eintrag.playerid)}>
                <span className="dhpt-sr-listen-pos">{index + 1}</span>
                <span className="dhpt-sr-listen-mitte">
                    <span className="dhpt-sr-listen-name">{spielerName(eintrag.playerid)}</span>
                    <span className="dhpt-sr-listen-werte">{werte}</span>
                </span>
                <span className="dhpt-sr-listen-punkte">{eintrag.punkte}<span className="dhpt-sr-listen-punkte-label">Pkt</span></span>
            </li>
        })}
    </ol>
}

// Gruppe des angemeldeten Benutzers (bei Doppel über die Doppelpaarung), sonst undefined
function ermittleMeineGruppe(tournament, userId) {
    if (!userId || !tournament.tournamentGroupEntities) {
        return undefined;
    }
    let player;
    if (tournament.doppelturnier) {
        const doppel = (tournament.doppelpartnerDtos || []).find(dto => dto.userId1 === userId || dto.userId2 === userId);
        player = doppel ? tournament.tournamentPlayerEntities.find(p => p.doppelpartnerId === doppel.id) : undefined;
    } else {
        player = tournament.tournamentPlayerEntities.find(p => p.userId === userId);
    }
    if (!player) {
        return undefined;
    }
    const eintrag = tournament.tournamentGroupEntities.find(group => group.spieler === player.id);
    return eintrag ? eintrag.gruppe : undefined;
}

function vorhanden(wert) {
    return wert !== null && wert !== undefined && wert !== "";
}
